import React, { useMemo, useState } from "react";
import Select, { components as selectComponents } from "react-select";
import { displayTwoDecimalDigits } from "../../global/Utils";
import { formatMoney } from "../../global/money";
import { itemDisplayName, SERIAL_LABEL } from "../../global/itemDisplay";
import {
  formatQty,
  isStockTracked,
  itemStockState,
  stockChipStyle,
  unitOf,
} from "../../global/stock";

/**
 * Item picker for invoice lines: a searchable dropdown whose menu opens as a
 * full-width grid - one row per item, with its serial, name, HS code, UOM,
 * unit price and (when asked for) stock in columns under a header - instead of
 * a one-line list.
 *
 * It keeps SearchableSelect's contract, so the screens' handleChange is not
 * touched: onChange receives a native-select-shaped event
 *   e.target.name / e.target.value          - the item's _id
 *   e.target.tagName === "SELECT"           - keeps upperCaseInput() off it
 *   e.target.selectedOptions[0].text        - the item's display name
 *
 * Search runs over the complete list (name, serial and HS code) and the
 * matches are paged on scroll, exactly as SearchableSelect pages.
 */

/** Rows rendered per page of the grid. */
const DEFAULT_PAGE_SIZE = 50;

/** How close (px) to the bottom of the grid the next page starts loading. */
const LOAD_MORE_THRESHOLD = 40;

/** Space (px) kept between the grid and the edges of the window. */
const VIEWPORT_GUTTER = 24;

/** Below this width the grid stops at the field and spans the window instead. */
const MIN_MENU_WIDTH = 640;

/** Gap (px) between the grid's columns, and its side padding. */
const COLUMN_GAP = 12;
export const ROW_PADDING_X = 16;

const EMPTY_OPTION = { value: "", label: "" };
const NO_IDS = [];

/**
 * The grid's columns. The serial only gets one when the company's "Concat
 * serial number with Product Name" setting would show it, and stock only on
 * screens that show stock.
 */
const gridColumnsFor = ({ withSerial, showStock }) =>
  [
    withSerial && {
      key: "serial",
      header: SERIAL_LABEL,
      min: 130,
      track: "1fr",
    },
    { key: "name", header: "Item Name", min: 220, track: "3fr" },
    { key: "hsCode", header: "HS Code", min: 110, track: "1fr" },
    { key: "uom", header: "UOM", min: 140, track: "1.5fr" },
    {
      key: "unitPrice",
      header: "Unit Price",
      min: 100,
      track: "1fr",
      align: "right",
    },
    showStock && { key: "stock", header: "Stock", min: 150, track: "1fr" },
  ].filter(Boolean);

/**
 * The CSS grid one row of the menu - header or item - is laid out on. Shared
 * with ScenarioGridSelect so both grids line up the same way.
 */
export const gridRowStyle = (columns) => ({
  display: "grid",
  gridTemplateColumns: columns
    .map((column) => `minmax(${column.min}px, ${column.track})`)
    .join(" "),
  columnGap: COLUMN_GAP,
  alignItems: "center",
  minWidth:
    columns.reduce((total, column) => total + column.min, 0) +
    COLUMN_GAP * (columns.length - 1) +
    ROW_PADDING_X * 2,
});

/**
 * Where the menu goes: from the field's left edge to the right edge of the
 * window, so the grid opens across the whole item line. When that leaves too
 * little room (a phone, a narrow window) it spans the window instead and
 * scrolls sideways.
 */
export const gridMenuBox = (rect) => {
  const viewport =
    document.documentElement?.clientWidth || window.innerWidth || 0;
  if (!rect || !viewport) return {};

  const toRightEdge = viewport - rect.left - VIEWPORT_GUTTER;
  const fullWidth = viewport - VIEWPORT_GUTTER * 2;
  if (toRightEdge >= Math.min(MIN_MENU_WIDTH, fullWidth)) {
    return { left: rect.left, width: Math.max(rect.width, toRightEdge) };
  }
  return { left: VIEWPORT_GUTTER, width: Math.max(rect.width, fullWidth) };
};

/**
 * What the field shows once an item is picked - the same text the old
 * dropdown showed: the display name, followed on screens that show stock by
 * what is on hand. A service product has none, so nothing follows its name.
 */
const optionLabel = (item, company, showStock) => {
  const name = itemDisplayName(item, company);
  return showStock && isStockTracked(item)
    ? `${name} — stock ${formatQty(item.currentStock, unitOf(item))}`
    : name;
};

const formatPrice = (value) =>
  displayTwoDecimalDigits(value) === "" ? "" : formatMoney(value);

/** A service product has no stock, so its cell stays empty. */
const StockCell = ({ item }) => {
  const state = itemStockState(item);
  if (!state) return null;
  const chip = stockChipStyle(state);
  return (
    <span
      className="inline-block text-xs font-bold px-2 py-0.5 rounded whitespace-nowrap"
      style={{ backgroundColor: chip.bg, color: chip.fg }}
      title={chip.label}
    >
      {formatQty(item.currentStock, unitOf(item))}
    </span>
  );
};

const cellContent = (column, item, isDisabled) => {
  switch (column.key) {
    case "serial":
      return String(item?.serial ?? "").trim();
    case "name":
      return (
        <>
          {item?.name}
          {isDisabled && (
            <span className="block text-xs font-normal normal-case text-gray-500">
              Already added on another line
            </span>
          )}
        </>
      );
    case "hsCode":
      return item?.HSCodeId?.pctCode ?? "";
    case "uom":
      return item?.uom ?? "";
    case "unitPrice":
      return formatPrice(item?.unitPrice);
    case "stock":
      return <StockCell item={item} />;
    default:
      return "";
  }
};

const cellClass = (column) =>
  ["min-w-0 break-words", column.align === "right" ? "text-right" : ""]
    .filter(Boolean)
    .join(" ");

/** One item as a row of the grid. */
const GridOption = (props) => {
  const { data, isDisabled, selectProps } = props;
  return (
    <selectComponents.Option {...props}>
      {selectProps.gridColumns.map((column) => (
        <div key={column.key} className={cellClass(column)}>
          {cellContent(column, data.item, isDisabled)}
        </div>
      ))}
    </selectComponents.Option>
  );
};

/**
 * The grid itself: a header that stays put while the rows scroll under it,
 * and the next page of rows appended when scrolled near the end. It reads its
 * state from selectProps so the component identity stays stable - swapping it
 * on every page would remount the list and jump back to the top.
 */
const GridMenuList = (props) => {
  const { onLoadMore, pagingInfo, gridColumns } = props.selectProps;

  const handleScroll = (event) => {
    const list = event.currentTarget;
    if (
      list.scrollHeight - list.scrollTop - list.clientHeight <=
      LOAD_MORE_THRESHOLD
    ) {
      onLoadMore?.();
    }
  };

  return (
    <selectComponents.MenuList
      {...props}
      innerProps={{ ...props.innerProps, onScroll: handleScroll }}
    >
      <div
        aria-hidden="true"
        className="sticky top-0 z-10 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600 bg-gray-100 border-b border-gray-200"
        style={{
          ...gridRowStyle(gridColumns),
          paddingLeft: ROW_PADDING_X,
          paddingRight: ROW_PADDING_X,
        }}
      >
        {gridColumns.map((column) => (
          <div key={column.key} className={cellClass(column)}>
            {column.header}
          </div>
        ))}
      </div>
      {props.children}
      {pagingInfo?.hasMore && (
        <div className="px-4 py-2 text-center text-xs normal-case text-gray-500">
          Showing {pagingInfo.shown} of {pagingInfo.total} - scroll for more
        </div>
      )}
    </selectComponents.MenuList>
  );
};

const SELECT_COMPONENTS = {
  IndicatorSeparator: null,
  MenuList: GridMenuList,
  Option: GridOption,
};

export const ItemGridSelect = ({
  name,
  value,
  onChange,
  onBlur,
  items,
  company,
  usedIds = NO_IDS,
  showStock = false,
  disabled = false,
  className = "",
  placeholder,
  maxMenuHeight = 420,
  noOptionsMessage = "No matches found",
  pageSize = DEFAULT_PAGE_SIZE,
}) => {
  const list = useMemo(() => (Array.isArray(items) ? items : []), [items]);

  // An item already on another line is disabled here, as before.
  const options = useMemo(
    () =>
      list.map((item) => ({
        value: item._id,
        label: optionLabel(item, company, showStock),
        item,
        isDisabled:
          usedIds.includes(item._id) &&
          String(item._id) !== String(value ?? ""),
        search: [
          itemDisplayName(item, company),
          item?.serial,
          item?.HSCodeId?.pctCode,
        ]
          .filter(Boolean)
          .join(" | ")
          .toLowerCase(),
      })),
    [list, company, usedIds, value, showStock],
  );

  const gridColumns = useMemo(
    () =>
      gridColumnsFor({
        // The display name only differs from the name when the company's
        // setting puts the serial in front of it.
        withSerial: list.some(
          (item) => itemDisplayName(item, company) !== (item?.name ?? ""),
        ),
        showStock,
      }),
    [list, company, showStock],
  );
  const rowStyle = useMemo(() => gridRowStyle(gridColumns), [gridColumns]);

  const selected =
    options.find((option) => String(option.value) === String(value ?? "")) ??
    null;

  const [query, setQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(pageSize);

  // Search the complete list, then page the matches.
  const matches = useMemo(() => {
    const search = query.trim().toLowerCase();
    if (!search) return options;
    return options.filter((option) => option.search.includes(search));
  }, [options, query]);

  const visibleOptions = useMemo(
    () => matches.slice(0, visibleCount),
    [matches, visibleCount],
  );
  const hasMore = visibleOptions.length < matches.length;

  const loadMore = () => {
    if (!hasMore) return;
    setVisibleCount((count) =>
      count >= matches.length ? count : count + pageSize,
    );
  };

  const handleInputChange = (input, { action }) => {
    if (action === "input-change" || input !== query) {
      setQuery(input);
      setVisibleCount(pageSize);
    }
  };

  const hasError = className.includes("border-red-500");

  // The same event SearchableSelect raises, so handleChange cannot tell the
  // two apart.
  const handleChange = (option) => {
    if (!onChange) return;
    const picked = option || EMPTY_OPTION;
    const dataset = { id: "" };
    const optionElement = {
      value: picked.value,
      label: picked.label,
      text: picked.label,
      dataset,
    };
    const target = {
      name,
      value: picked.value,
      type: "select-one",
      tagName: "SELECT",
      dataset,
      options: optionElement.value === "" ? [] : [optionElement],
      selectedOptions: [optionElement],
      selectedIndex: option ? 0 : -1,
    };
    onChange({
      target,
      currentTarget: target,
      preventDefault: () => {},
      stopPropagation: () => {},
    });
  };

  return (
    <Select
      unstyled
      inputId={name ? `searchable-select-${name}` : undefined}
      name={name}
      value={selected}
      options={visibleOptions}
      filterOption={null}
      onInputChange={handleInputChange}
      onMenuOpen={() => setVisibleCount(pageSize)}
      onLoadMore={loadMore}
      pagingInfo={{
        hasMore,
        shown: visibleOptions.length,
        total: matches.length,
      }}
      gridColumns={gridColumns}
      onChange={handleChange}
      onBlur={onBlur}
      isDisabled={disabled}
      isClearable
      isSearchable
      placeholder={
        placeholder ?? (list.length === 0 ? "No Items Available" : "Select")
      }
      maxMenuHeight={maxMenuHeight}
      menuPlacement="auto"
      menuPortalTarget={document.body}
      noOptionsMessage={() => noOptionsMessage}
      components={SELECT_COMPONENTS}
      styles={{
        menuPortal: (base, { rect }) => ({
          ...base,
          zIndex: 9999,
          ...gridMenuBox(rect),
        }),
        option: (base) => ({
          ...base,
          ...rowStyle,
          paddingLeft: ROW_PADDING_X,
          paddingRight: ROW_PADDING_X,
        }),
      }}
      classNames={{
        // The caller's classes keep the field looking exactly like the
        // dropdown it replaced (width, padding, radius, error border).
        control: ({ isFocused }) =>
          [
            className,
            "flex items-center gap-2 bg-white cursor-pointer transition-colors",
            isFocused && !hasError ? "border-blue-500" : "",
            disabled ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "",
          ]
            .filter(Boolean)
            .join(" "),
        valueContainer: () => "flex flex-1 items-center overflow-hidden",
        singleValue: () => "truncate text-gray-900",
        placeholder: () => "truncate text-gray-400",
        input: () => "m-0 p-0 text-gray-900",
        indicatorsContainer: () => "flex items-center gap-1",
        dropdownIndicator: () => "text-gray-500 hover:text-gray-700",
        clearIndicator: () => "text-gray-400 hover:text-gray-600 cursor-pointer",
        menu: () =>
          "mt-1 overflow-hidden rounded-md border border-gray-200 bg-white shadow-xl",
        menuList: () => "overflow-x-auto",
        option: ({ isSelected, isFocused, isDisabled }) =>
          [
            "py-2 text-sm border-b border-gray-100",
            isDisabled
              ? "text-gray-400 bg-gray-50 cursor-not-allowed"
              : "cursor-pointer text-gray-900",
            isSelected
              ? "bg-blue-600 text-white"
              : isFocused && !isDisabled
                ? "bg-blue-50"
                : "",
          ]
            .filter(Boolean)
            .join(" "),
        noOptionsMessage: () => "px-4 py-3 text-sm text-gray-500",
      }}
    />
  );
};

export default ItemGridSelect;
